// Completeness check (Round 12, Part 3): the deterministic backstop for the
// one risk verification can't catch by design — an amount or date the AI
// simply FAILED to extract. Quote-substring verification proves every stored
// fact is real; it says nothing about what was missed. So we scan each
// record's raw text for money/date patterns and flag any record that contains
// them yet produced ZERO amount (resp. date) facts. We do NOT auto-extract —
// we only make the gap visible for human review.
//
// Tuned to under-flag rather than over-flag: $-amounts and thousands-grouped
// figures always count as money; a bare "N.NN" counts only with money context
// (a currency symbol or a money word on the same line), so version numbers and
// coordinates don't trip it.
//
// Pure functions, no React, no AI — unit-tested via node --test.

import type { RecordFact } from "../types";

export interface ScannableRecord {
  id: string;
  subject?: string | null;
  record_date?: string | null;
  body_text?: string | null;
  ocr_text?: string | null;
}

export interface GapFlag {
  record_id: string;
  subject: string | null;
  record_date: string | null;
  // The record's text shows money, but no amount fact was extracted.
  moneyGap: boolean;
  // The record's text shows date(s), but no dated fact was extracted.
  dateGap: boolean;
  // The literal candidate strings found (deduped, capped) — shown to the
  // reviewer so they can see exactly what looks unextracted.
  moneyCandidates: string[];
  dateCandidates: string[];
}

const MONEY_WORDS =
  /\b(?:usd|gbp|eur|paid|pay|cost|costs|invoice|invoiced|total|subtotal|refund|fee|fees|charge|charged|deductible|balance|amount|price|priced|bill|billed|owed|owe|due|estimate|estimated|quote|quoted|deposit|premium|reimburse\w*|settlement|payout)\b/i;

// $-prefixed and thousands-grouped figures are unambiguous money. Fresh
// RegExp per use — these are stateful (global flag) and shared reuse across
// matchAll/replace would leak lastIndex.
const currencySymbolRe = () => /[$£€]\s?\d[\d,]*(?:\.\d+)?/g;
const thousandsRe = () => /\b\d{1,3}(?:,\d{3})+(?:\.\d{2})?\b/g;
// A bare two-decimal figure — only money WITH context (see scanMoney).
const bareDecimalRe = () => /\b\d+\.\d{2}\b/g;

// ISO, slash, and "Month DD, YYYY" date shapes. Fresh RegExp per use (global).
const ISO_DATE_RE = () => /\b\d{4}-\d{2}-\d{2}\b/g;
const SLASH_DATE_RE = () => /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g;
const MONTH_NAME_RE = () =>
  /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\.?\s+\d{1,2}(?:st|nd|rd|th)?,?\s+\d{4}\b/gi;

function dedupeCap(values: string[], cap = 12): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const t = v.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
    if (out.length >= cap) break;
  }
  return out;
}

export function scanMoney(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(currencySymbolRe())) out.push(m[0]);
  // Strip the $-amounts first so the thousands pass doesn't re-match their
  // numeric tail ("1,050.00" inside "$1,050.00").
  const noCurrency = text.replace(currencySymbolRe(), " ");
  for (const m of noCurrency.matchAll(thousandsRe())) out.push(m[0]);
  // Bare decimals: accept per-line only when the line carries money context —
  // and only AFTER removing the $-amounts / thousands figures already counted,
  // so we never double-count the numeric tail of "$135.60".
  for (const line of text.split(/\r?\n/)) {
    const hasContext = MONEY_WORDS.test(line) || /[$£€]/.test(line);
    if (!hasContext) continue;
    const residual = line
      .replace(currencySymbolRe(), " ")
      .replace(thousandsRe(), " ");
    for (const m of residual.matchAll(bareDecimalRe())) out.push(m[0]);
  }
  return dedupeCap(out);
}

export function scanDates(text: string): string[] {
  const out: string[] = [];
  for (const re of [ISO_DATE_RE(), SLASH_DATE_RE(), MONTH_NAME_RE()]) {
    for (const m of text.matchAll(re)) out.push(m[0]);
  }
  return dedupeCap(out);
}

// Build the gap report. A record is money-gapped when its text shows money but
// it produced no verified amount fact; date-gapped when its text shows dates
// but no fact carries a date. Records with neither gap are omitted.
export function scanForExtractionGaps(
  records: ScannableRecord[],
  facts: RecordFact[],
): GapFlag[] {
  const amountByRecord = new Map<string, number>();
  const datedByRecord = new Map<string, number>();
  for (const f of facts) {
    if (!f.verified) continue;
    if (f.fact_type === "amount") {
      amountByRecord.set(f.record_id, (amountByRecord.get(f.record_id) ?? 0) + 1);
    }
    if (f.date || f.deadline || f.fact_type === "deadline") {
      datedByRecord.set(f.record_id, (datedByRecord.get(f.record_id) ?? 0) + 1);
    }
  }

  const flags: GapFlag[] = [];
  for (const rec of records) {
    const text = `${rec.body_text ?? ""}\n${rec.ocr_text ?? ""}`;
    if (!text.trim()) continue;
    const moneyCandidates = scanMoney(text);
    const dateCandidates = scanDates(text);
    const moneyGap = moneyCandidates.length > 0 && !amountByRecord.has(rec.id);
    const dateGap = dateCandidates.length > 0 && !datedByRecord.has(rec.id);
    if (!moneyGap && !dateGap) continue;
    flags.push({
      record_id: rec.id,
      subject: rec.subject ?? null,
      record_date: rec.record_date ?? null,
      moneyGap,
      dateGap,
      moneyCandidates: moneyGap ? moneyCandidates : [],
      dateCandidates: dateGap ? dateCandidates : [],
    });
  }
  return flags;
}
